// Builder streak ledger. A "streak" is the number of consecutive
// calendar days (local time) on which the user has successfully built
// at least one app. The longest active reward loop in the product —
// "don't break the chain" — without any backend.
//
// Storage shape (localStorage `pomela:streak:v1`):
//   { lastBuildDay: 'YYYY-MM-DD', current: number, best: number }
//
// Rules:
//   - First build → current = 1.
//   - Same calendar day → no change (you can build many times today).
//   - Next calendar day → current += 1.
//   - Skip ≥1 calendar day → current resets to 1.
//   - `best` is the all-time high; it never resets.
//   - readStreak() also returns whether the streak is `stale` (the user
//     hasn't built today yet), which the UI uses to render a softer state.

const LS_KEY = 'pomela:streak:v1';

interface RawStreak {
  lastBuildDay: string;
  current: number;
  best: number;
}

export interface Streak {
  current: number;
  best: number;
  /** True if the user hasn't built yet today (streak is at risk of breaking). */
  stale: boolean;
  /** ISO YYYY-MM-DD of the most recent recorded build, or null. */
  lastDay: string | null;
}

/** Local-time calendar day, formatted ISO without time. */
function todayKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Difference in whole calendar days between two YYYY-MM-DD strings. */
function daysBetween(aIso: string, bIso: string): number {
  const a = Date.parse(`${aIso}T00:00:00`);
  const b = Date.parse(`${bIso}T00:00:00`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.POSITIVE_INFINITY;
  return Math.round((b - a) / 86400000);
}

function readRaw(): RawStreak | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RawStreak;
    if (
      typeof parsed?.lastBuildDay === 'string' &&
      typeof parsed?.current === 'number' &&
      typeof parsed?.best === 'number'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeRaw(value: RawStreak): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked — streak is best-effort, not load-bearing */
  }
}

/**
 * Record a successful build today. Idempotent within the same calendar
 * day. Returns the updated streak (callers can show a "streak +1!" toast
 * by diffing against their previous read).
 */
export function recordBuildToday(): Streak {
  const today = todayKey();
  const prev = readRaw();
  if (!prev) {
    const seeded: RawStreak = { lastBuildDay: today, current: 1, best: 1 };
    writeRaw(seeded);
    return { current: 1, best: 1, stale: false, lastDay: today };
  }
  const gap = daysBetween(prev.lastBuildDay, today);
  if (gap === 0) {
    // Already counted today — no change.
    return {
      current: prev.current,
      best: prev.best,
      stale: false,
      lastDay: prev.lastBuildDay
    };
  }
  const nextCurrent = gap === 1 ? prev.current + 1 : 1;
  const nextBest = Math.max(prev.best, nextCurrent);
  const next: RawStreak = { lastBuildDay: today, current: nextCurrent, best: nextBest };
  writeRaw(next);
  return { current: nextCurrent, best: nextBest, stale: false, lastDay: today };
}

/** Read the streak without mutating it. Pure side-effect-free getter. */
export function readStreak(): Streak {
  const prev = readRaw();
  if (!prev) return { current: 0, best: 0, stale: true, lastDay: null };
  const today = todayKey();
  const gap = daysBetween(prev.lastBuildDay, today);
  if (gap === 0) {
    return { current: prev.current, best: prev.best, stale: false, lastDay: prev.lastBuildDay };
  }
  if (gap === 1) {
    // Yesterday's streak is still alive — they can build today to keep it.
    return { current: prev.current, best: prev.best, stale: true, lastDay: prev.lastBuildDay };
  }
  // Two or more days off — the streak is broken in displayed state, but
  // we don't WRITE the reset until they actually build (lets them see
  // their old chain on the day they return).
  return { current: 0, best: prev.best, stale: true, lastDay: prev.lastBuildDay };
}

/** Reset the streak ledger entirely. Use from "delete my data" flows. */
export function clearStreak(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* swallow */
  }
}
